'use client';

import { TapLink } from '../tap-link';
import { useId, useState, type ReactNode } from 'react';
import { CaretRight } from '@/components/icons';
import styles from './disclosure.module.css';

/**
 * Disclosure - the section fold. Two folding modes:
 *
 * - `fold="compact"` (default): the original compact-viewport fold (docs/
 *   MOBILE.md §3, item detail). On a phone, secondary item sections (Fields,
 *   Relationships, Activity, …) render as a quiet tappable row that expands in
 *   place; on desktop the fold does not exist - the toggle row is CSS-hidden
 *   and the children render exactly where they always did (root and body are
 *   `display: contents`, so parent flex/grid layouts are untouched).
 * - `fold="always"`: the same quiet row folds on EVERY viewport - the
 *   read-at-rest / edit-on-demand section shape (DESIGN_SYSTEM.md "Authoring
 *   surfaces"; first user: the item page's Access section, whose at-rest face
 *   is the row itself).
 *
 * Structure switches in CSS (the MOBILE.md rendering rule): both the toggle
 * and the body are always in the DOM; `@media` / the `data-fold` variant gate
 * them. The open state is client-side and defaults closed.
 *
 * `accessory` renders after the label at rest (e.g. the access section's
 * avatar bubbles); `detail` stays the right-aligned digest.
 *
 * `href` makes the compact row a **door** (docs/MOBILE.md §3, band 3): the row
 * is a link to the section's own screen instead of a toggle, because a section
 * that spawns its own overlays cannot live in a fold under a thumb (a sheet
 * inside a sheet takes no taps on iOS). The row anatomy is identical and the
 * desktop rendering is untouched - the body still renders in place there, the
 * link exists only on compact, where it never opens anything inline.
 *
 * `icon` leads the compact row with the section's glyph (the phone item
 * page's list of doors, 2026-11-02, docs/MOBILE.md §3): 18px in the muted
 * ink, so a row is known before it is read. `null` keeps the slot empty, so
 * a row with no glyph of its own keeps its label on the others' edge.
 */
export function Disclosure({
  label,
  count,
  detail,
  accessory,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  fold = 'compact',
  href,
  icon,
  testId,
  children,
}: {
  label: string;
  /** A quiet "is there anything inside" signal on the folded row. */
  count?: number;
  /** A right-aligned digest on the folded row (e.g. "2/5 sub-items done"),
   *  so the fold carries its headline without being opened. */
  detail?: ReactNode;
  /** Rendered after the label on the row (e.g. avatar bubbles). */
  accessory?: ReactNode;
  defaultOpen?: boolean;
  /** Controlled open state, for a host that opens the fold itself (a form
   *  that shows a problem inside it). With it, `onOpenChange` gets every
   *  toggle and `defaultOpen` is ignored. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 'compact' folds only on phones (desktop renders children in place);
   *  'always' keeps the fold on every viewport. */
  fold?: 'compact' | 'always';
  /** The section's own screen: the compact row becomes a link to it and opens
   *  nothing in place. Desktop is unaffected. */
  href?: string;
  /** The row's leading glyph; `null` reserves the slot with nothing in it. */
  icon?: ReactNode;
  /** Lands on the tappable row (the toggle, or the door link). */
  testId?: string;
  children: ReactNode;
}) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };
  const bodyId = useId();
  const door = href !== undefined;
  const row = (
    <>
      <CaretRight className={styles.caret} aria-hidden />
      {icon !== undefined && (
        <span className={styles.icon} aria-hidden>
          {icon}
        </span>
      )}
      <span className={styles.label}>{label}</span>
      {count !== undefined && count > 0 && <span className={styles.count}>{count}</span>}
      {accessory !== undefined && <span className={styles.accessory}>{accessory}</span>}
      {detail !== undefined && <span className={styles.detail}>{detail}</span>}
    </>
  );
  return (
    <div
      className={styles.root}
      data-open={(!door && open) || undefined}
      data-fold={fold === 'always' ? 'always' : undefined}
      data-door={door || undefined}
    >
      {door ? (
        <TapLink href={href} tap={{ title: label }} className={styles.toggle} data-testid={testId}>
          {row}
        </TapLink>
      ) : (
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={open}
          aria-controls={bodyId}
          data-testid={testId}
          onClick={() => setOpen(!open)}
        >
          {row}
        </button>
      )}
      <div id={bodyId} className={styles.body}>
        {children}
      </div>
    </div>
  );
}
